<template>
  <b-modal size="xl" v-model="showForm" :title="$t('modalTitle')" no-close-on-backdrop no-enforce-focus>
    <!-- Banner trạng thái -->
    <div v-if="formModel.confirmTime" class="alert alert-success py-2 mb-3">
      <i class="fa fa-check-circle mr-1"></i>
      <strong>{{ $t('alreadyConfirmed') }}</strong>
      — {{ formModel.confirmTime | moment('DD/MM/YY HH:mm') }}
      <span v-if="formModel.confirmedBy">— {{ formModel.confirmedBy.name }}</span>
    </div>
    <div v-else-if="formModel.cancelTime" class="alert alert-danger py-2 mb-3">
      <i class="fa fa-times-circle mr-1"></i>
      <strong>{{ $t('alreadyCanceled') }}</strong>
      — {{ formModel.cancelTime | moment('DD/MM/YY HH:mm') }}
      <span v-if="formModel.canceledBy">— {{ formModel.canceledBy.name }}</span>
      <div v-if="formModel.note" class="mt-1 small"><strong>{{ $t('reason') }}:</strong> {{ formModel.note }}</div>
    </div>

    <!-- Validation banner: tự động phân tích hồ sơ -->
    <div v-if="canReview && !errorIssues.length && !warningIssues.length" class="alert alert-success py-2 mb-3">
      <i class="fa fa-check-circle mr-1"></i>
      <strong>{{ $t('readyToApprove') }}</strong>
    </div>
    <div v-else-if="canReview && errorIssues.length" class="alert alert-danger py-2 mb-3">
      <div><i class="fa fa-exclamation-circle mr-1"></i>
        <strong>{{ $t('missingRequired', [errorIssues.length]) }}</strong>
        <span class="text-muted small">{{ $t('missingRequiredHint') }}</span>
      </div>
      <ul class="mb-0 mt-1 small">
        <li v-for="(issue, idx) in errorIssues" :key="'e' + idx">{{ issue }}</li>
      </ul>
    </div>
    <div v-else-if="canReview && warningIssues.length" class="alert alert-warning py-2 mb-3">
      <div><i class="fa fa-exclamation-triangle mr-1"></i>
        <strong>{{ $t('warningsFound', [warningIssues.length]) }}</strong>
        <span class="text-muted small">{{ $t('warningsHint') }}</span>
      </div>
      <ul class="mb-0 mt-1 small">
        <li v-for="(issue, idx) in warningIssues" :key="'w' + idx">{{ issue }}</li>
      </ul>
    </div>

    <!-- Preview ảnh hồ sơ định danh -->
    <fieldset class="kyc-section mb-3">
      <legend class="kyc-legend"><i class="fa fa-id-card mr-1"></i>{{ $t('kycImagesPreview') }}</legend>
      <div v-if="imageLoading" class="text-center py-3">
        <b-spinner small></b-spinner>
      </div>
      <div v-else class="row">
        <div v-if="formModel.type === 'enterprise' && ['image/jpeg', 'image/jpg'].includes(formModel.businessRegistrationType)" class="col-md-3 col-6 pb-2">
          <b-card :sub-title="$t('businessRegistration')"
              :img-src="formModel.businessRegistrationUrl || '/img/default-avatar.png'"
              body-class="pb-1" img-bottom tag="a"
              :href="formModel.businessRegistrationUrl" target="_blank">
          </b-card>
        </div>
        <div class="col-md-3 col-6 pb-2">
          <b-card :sub-title="$t('owner')"
              :img-src="formModel.ownerUrl || '/img/default-avatar.png'"
              body-class="pb-1" img-bottom tag="a"
              :href="formModel.ownerUrl" target="_blank">
          </b-card>
        </div>
        <div class="col-md-3 col-6 pb-2">
          <b-card :sub-title="$t('IDFront')"
              :img-src="formModel.IDFrontUrl || '/img/default-avatar.png'"
              body-class="pb-1" img-bottom tag="a"
              :href="formModel.IDFrontUrl" target="_blank">
          </b-card>
        </div>
        <div class="col-md-3 col-6 pb-2">
          <b-card :sub-title="$t('IDBack')"
              :img-src="formModel.IDBackUrl || '/img/default-avatar.png'"
              body-class="pb-1" img-bottom tag="a"
              :href="formModel.IDBackUrl" target="_blank">
          </b-card>
        </div>
        <div v-if="formModel.type === 'personal'" class="col-md-3 col-6 pb-2">
          <b-card :sub-title="$t('proofOfAddress')"
              :img-src="formModel.proofOfAddressUrl || '/img/default-avatar.png'"
              body-class="pb-1" img-bottom tag="a"
              :href="formModel.proofOfAddressUrl" target="_blank">
          </b-card>
        </div>
        <div v-if="formModel.type === 'enterprise' && !['image/jpeg', 'image/jpg'].includes(formModel.businessRegistrationType)" class="col-md-3 col-6 pb-2">
          <a :href="formModel.businessRegistrationUrl" target="_blank" class="btn btn-outline-info is-border-1 btn-block btn-sm">
            <i class="fa fa-file-pdf mr-1"></i>{{ $t('businessRegistration') }}
          </a>
        </div>
        <div v-if="formModel.contract && formModel.sendContractTime" class="col-md-3 col-6 pb-2">
          <a :href="formModel.contractUrl" target="_blank" class="btn btn-outline-success is-border-1 btn-block btn-sm">
            <i class="fa fa-file-signature mr-1"></i>{{ $t('contractSigned') }}
          </a>
        </div>
      </div>
    </fieldset>

    <!-- Thông tin chung: Client + Loại khách (full width trên cùng) -->
    <fieldset class="kyc-section mb-3">
      <legend class="kyc-legend"><i class="fa fa-info-circle mr-1"></i>{{ $t('generalInfo') }}</legend>
      <div class="row">
        <div class="col-md-6">
          <client-select :disabled="formModel._id ? true : false" :label="$t('client')" :model="formModel"
                         attribute="client" :errors="formErrors"></client-select>
        </div>
        <div class="col-md-6">
          <form-select :label="$t('type')" :model="formModel" attribute="type" :full-width="true" :options="[
              {value: 'personal', text: $t('personal')},
              {value: 'enterprise', text: $t('enterprise')}
            ]">
          </form-select>
        </div>
      </div>
    </fieldset>

    <div class="row">
      <!-- ============== CỘT TRÁI col-7: PHÁP LÝ + HOÁ ĐƠN ============== -->
      <div class="col-lg-7">
        <fieldset class="kyc-section mb-3">
          <legend class="kyc-legend"><i class="fa fa-balance-scale mr-1"></i>{{ $t('legalInfo') }}</legend>
          <template v-if="formModel.type === 'enterprise'">
            <h6 class="kyc-subheader">{{ $t('companyInfo') }}</h6>
            <div class="row">
              <div class="col-md-6">
                <form-input :label="$t('companyName')" :model="formModel" attribute="companyName"
                            :errors="formErrors"></form-input>
              </div>
              <div class="col-md-6">
                <form-input :label="$t('shortName')" :model="formModel" attribute="shortName"
                            :errors="formErrors"></form-input>
              </div>
              <div class="col-md-6">
                <form-input :label="$t('taxCode')" :model="formModel" attribute="companyTaxCode"
                            :errors="formErrors"></form-input>
              </div>
              <div class="col-md-6">
                <form-input :label="$t('address')" :model="formModel" attribute="companyAddress"
                            :errors="formErrors"></form-input>
              </div>
            </div>
          </template>
          <h6 class="kyc-subheader">{{ formModel.type === 'enterprise' ? $t('ownerInfo') : $t('personalInfo') }}</h6>
          <div class="row">
            <div class="col-md-6">
              <form-input :label="$t('fullName')" :model="formModel" attribute="name" :errors="formErrors"></form-input>
            </div>
            <div v-if="formModel.type === 'enterprise'" class="col-md-6">
              <form-input :label="$t('position')" :model="formModel"
                          attribute="position" :errors="formErrors"></form-input>
            </div>
            <div class="col-md-6">
              <form-input :label="$t('personalTaxCodeLabel')" :model="formModel" attribute="taxCode" :errors="formErrors"></form-input>
            </div>
            <div class="col-md-6">
              <form-input :label="$t('personalAddressLabel')" :model="formModel" attribute="address" :errors="formErrors"></form-input>
            </div>
            <div class="col-md-12">
              <form-input :label="$t('signerEmail')" :model="formModel" attribute="signerEmail"
                          :errors="formErrors"></form-input>
            </div>
          </div>
        </fieldset>

        <fieldset class="kyc-section">
          <legend class="kyc-legend"><i class="fa fa-file-invoice mr-1"></i>{{ $t('invoiceInformation') }}</legend>
          <form-checkbox :chk-label="$t('invoiceEnabled')" :model="formModel"
                         attribute="invoiceEnabled"></form-checkbox>
          <form-array :label="$t('invoiceEmails')" :model="formModel" attribute="invoiceEmails"
                      :errors="formErrors"></form-array>
        </fieldset>
      </div>

      <!-- ============== CỘT PHẢI col-5: ẢNH UPLOAD + KỊCH BẢN + DUYỆT ============== -->
      <div class="col-lg-5">
        <fieldset class="kyc-section mb-3">
          <legend class="kyc-legend"><i class="fa fa-folder-open mr-1"></i>{{ $t('kycInformation') }}</legend>
          <form-file v-if="formModel.type === 'enterprise'" :label="$t('businessRegistration')"
                     service="/upload/upload-kyc" :model="formModel" @change="getImageUrl('businessRegistration')"
                     attribute="businessRegistration" attribute-file-type="businessRegistrationType"
                     :errors="formErrors" accept="image/jpeg,image/jpg,application/pdf">
          </form-file>
          <form-file :label="$t('owner')" service="/upload/upload-kyc" :model="formModel" @change="getImageUrl('owner')"
                     attribute="owner" :errors="formErrors" accept="image/jpeg,image/jpg,application/pdf">
          </form-file>
          <form-file :label="$t('IDFront')" service="/upload/upload-kyc" :model="formModel"
                     @change="getImageUrl('IDFront')"
                     attribute="IDFront" :errors="formErrors" accept="image/jpeg,image/jpg,application/pdf">
          </form-file>
          <form-file :label="$t('IDBack')" service="/upload/upload-kyc" :model="formModel"
                     @change="getImageUrl('IDBack')"
                     attribute="IDBack" :errors="formErrors" accept="image/jpeg,image/jpg,application/pdf">
          </form-file>
          <form-file v-if="formModel.type === 'personal'" :label="$t('proofOfAddress')" service="/upload/upload-kyc"
                     :model="formModel" @change="getImageUrl('proofOfAddress')"
                     attribute="proofOfAddress" :errors="formErrors" accept="image/jpeg,image/jpg,application/pdf">
          </form-file>
        </fieldset>

        <fieldset class="kyc-section">
          <legend class="kyc-legend">
            <i class="fa fa-phone mr-1"></i>{{ $t('callScript') }}
            <span class="text-danger">*</span>
          </legend>
          <small class="form-text text-muted mb-2">{{ $t('callScriptHint') }}</small>
          <form-textarea :rows="4" attribute="callScript" :model="formModel" :errors="formErrors"
                         :placeholder="$t('callScriptPlaceholder')" />
        </fieldset>
      </div>
    </div>

    <!-- ============== QUYẾT ĐỊNH DUYỆT (full-width) ============== -->
    <fieldset class="kyc-section mt-3">
      <legend class="kyc-legend"><i class="fa fa-gavel mr-1"></i>{{ $t('reviewSection') }}</legend>
      <div class="row">
        <div class="col-md-5">
          <form-checkbox :chk-label="$t('offBuyNumber')" :model="formModel" attribute="offBuyNumber"
                         :errors="formErrors" />
          <div class="form-group mt-3 mb-0">
            <label class="font-weight-bold mb-1">{{ $t('reviewNote') }}</label>
            <small class="form-text text-muted mb-2">{{ $t('reviewNoteHint') }}</small>
            <form-textarea :rows="4" attribute="note" :model="formModel" :errors="formErrors"
                           :placeholder="$t('notePlaceholder')" />
          </div>
        </div>
        <div v-if="canReview" class="col-md-7">
          <label class="font-weight-bold mb-1">{{ $t('rejectTplLabel') }}</label>
          <small class="form-text text-muted mb-2">{{ $t('rejectTplHint') }}</small>
          <div class="d-flex flex-column" style="gap: 6px">
            <button v-for="tpl in rejectTemplates" :key="tpl.key" type="button"
                    class="btn btn-outline-secondary is-border-1 btn-sm text-left"
                    @click="useRejectTemplate(tpl)">
              <i class="fa fa-quote-left mr-1 text-muted"></i>{{ tpl.label }}
            </button>
          </div>
        </div>
      </div>
    </fieldset>

    <template v-slot:modal-footer>
      <div class="d-flex w-100 justify-content-between align-items-center">
        <button class="btn btn-secondary" @click="showForm = false">
          {{ $t('close') }}
        </button>
        <div v-if="canSave">
          <button class="btn btn-info mr-2" @click="onSave" :disabled="processing">
            <i class="fa fa-save mr-1"></i>{{ $t('saveOnly') }}
          </button>
          <button v-if="canReview" class="btn btn-danger mr-2" @click="onReject" :disabled="processing">
            <i class="fa fa-times mr-1"></i>{{ $t('reject') }}
          </button>
          <button v-if="canReview" class="btn btn-success" @click="onApprove" :disabled="processing">
            <i class="fa fa-check mr-1"></i>{{ $t('approve') }}
          </button>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import CRUD from "@/mixins/CRUD";
import ClientSelect from "@/components/ClientSelect.vue";

export default {
  components: {ClientSelect},
  mixins: [CRUD],
  data: function () {
    return {
      service: '/client-kyc',
      reloadBus: 'reloadKYC',
      defaultFormModel: {
        type: 'enterprise',
        invoiceEnabled: true,
        businessRegistrationType: 'image/jpeg',
        businessRegistration: null,
        IDFront: null,
        IDBack: null,
        owner: null,
        proofOfAddress: null,
        offBuyNumber: false,
      },
      imageLoading: false,
      processing: false,
    }
  },
  computed: {
    canSave() {
      return ['owner', 'admin', 'tech', 'account', 'accountant', 'operationsManager'].includes(this.$store.state.user.role);
    },
    canReview() {
      // Cho phép duyệt/từ chối khi chưa được duyệt + chưa bị từ chối
      return this.formModel._id && !this.formModel.confirmTime && !this.formModel.cancelTime;
    },
    /**
     * Vấn đề chặn duyệt — admin phải yêu cầu khách bổ sung trước khi duyệt
     */
    errorIssues() {
      const m = this.formModel;
      const issues = [];
      if (!m._id) return issues;
      if (!m.type) issues.push(this.$t('missing.type'));

      // Ảnh CCCD — bắt buộc cả 2 type
      if (!m.IDFront) issues.push(this.$t('missing.IDFront'));
      if (!m.IDBack) issues.push(this.$t('missing.IDBack'));
      if (!m.owner) issues.push(this.$t('missing.owner'));

      if (m.type === 'enterprise') {
        if (!m.businessRegistration) issues.push(this.$t('missing.businessRegistration'));
        if (!m.companyName) issues.push(this.$t('missing.companyName'));
        if (!m.companyTaxCode) issues.push(this.$t('missing.companyTaxCode'));
        if (!m.companyAddress) issues.push(this.$t('missing.companyAddress'));
        if (!m.position) issues.push(this.$t('missing.position'));
      } else if (m.type === 'personal') {
        if (!m.proofOfAddress) issues.push(this.$t('missing.proofOfAddress'));
      }

      if (!m.name) issues.push(this.$t(m.type === 'enterprise' ? 'missing.representativeName' : 'missing.fullName'));
      if (!m.address) issues.push(this.$t(m.type === 'enterprise' ? 'missing.representativeAddress' : 'missing.address'));
      if (!m.callScript || !m.callScript.trim()) issues.push(this.$t('missing.callScript'));

      // Hoá đơn: nếu enabled mà thiếu email → error
      if (m.invoiceEnabled && (!m.invoiceEmails || !m.invoiceEmails.length || !m.invoiceEmails[0])) {
        issues.push(this.$t('missing.invoiceEmail'));
      }
      // Personal + invoiceEnabled → required taxCode
      if (m.type === 'personal' && m.invoiceEnabled && !m.taxCode) {
        issues.push(this.$t('missing.personalTaxCode'));
      }
      return issues;
    },
    /**
     * Cảnh báo — không chặn duyệt nhưng admin nên review
     */
    warningIssues() {
      const m = this.formModel;
      const issues = [];
      if (!m._id) return issues;

      if (m.type === 'enterprise' && !m.shortName) {
        issues.push(this.$t('warning.shortName'));
      }
      if (!m.signerEmail) {
        issues.push(this.$t('warning.signerEmail'));
      }

      // Cross-check KYC vs Client (đăng ký)
      if (m.client) {
        if (m.type === 'enterprise' && m.companyName && m.client.companyName) {
          if (this.normalizeText(m.companyName) !== this.normalizeText(m.client.companyName)) {
            issues.push(this.$t('warning.companyNameMismatch', [m.client.companyName, m.companyName]));
          }
        }
        if (m.type === 'enterprise' && m.companyTaxCode && m.client.taxCode) {
          if (m.companyTaxCode.replace(/\s/g, '') !== m.client.taxCode.replace(/\s/g, '')) {
            issues.push(this.$t('warning.taxCodeMismatch', [m.client.taxCode, m.companyTaxCode]));
          }
        }
      }
      return issues;
    },
    /**
     * Templates lý do từ chối phổ biến — admin click để fill nhanh
     */
    rejectTemplates() {
      return [
        {key: 'idBlurry', label: this.$t('rejectTpl.idBlurry')},
        {key: 'taxCodeMismatch', label: this.$t('rejectTpl.taxCodeMismatch')},
        {key: 'companyNameMismatch', label: this.$t('rejectTpl.companyNameMismatch')},
        {key: 'addressIncomplete', label: this.$t('rejectTpl.addressIncomplete')},
        {key: 'businessRegistrationInvalid', label: this.$t('rejectTpl.businessRegistrationInvalid')},
      ];
    },
  },
  methods: {
    onSave() {
      this.$swal({
        title: this.$t('saveConfirmTitle'),
        text: this.$t('saveConfirmText'),
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('saveOnly'),
        cancelButtonText: this.$t('cancel'),
      }).then(result => {
        if (result.value) this.submit(null);
      });
    },
    onApprove() {
      // Chặn nếu có lỗi bắt buộc
      if (this.errorIssues.length) {
        this.$swal({
          title: this.$t('cannotApproveTitle'),
          html: this.$t('cannotApproveText') + '<ul style="text-align:left;margin-top:8px">'
              + this.errorIssues.map(i => '<li>' + i + '</li>').join('')
              + '</ul>',
          icon: 'error',
        });
        return;
      }
      // Có cảnh báo → confirm đặc biệt với danh sách
      if (this.warningIssues.length) {
        this.$swal({
          title: this.$t('approveWithWarningsTitle'),
          html: this.$t('approveWithWarningsText') + '<ul style="text-align:left;margin-top:8px">'
              + this.warningIssues.map(i => '<li>' + i + '</li>').join('')
              + '</ul>',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: this.$t('approveAnyway'),
          cancelButtonText: this.$t('cancel'),
        }).then(result => {
          if (result.value) this.submit('confirmed');
        });
        return;
      }
      // Bình thường
      this.$swal({
        title: this.$t('approveConfirmTitle'),
        text: this.$t('approveConfirmText'),
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('approve'),
        cancelButtonText: this.$t('cancel'),
      }).then(result => {
        if (result.value) this.submit('confirmed');
      });
    },
    useRejectTemplate(tpl) {
      const text = this.$t('rejectTpl.' + tpl.key);
      this.$set(this.formModel, 'note', text);
    },
    normalizeText(s) {
      return String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();
    },
    onReject() {
      if (!this.formModel.note || !this.formModel.note.trim()) {
        this.$swal({
          title: this.$t('rejectNoteRequiredTitle'),
          text: this.$t('rejectNoteRequiredText'),
          icon: 'warning',
        });
        return;
      }
      this.$swal({
        title: this.$t('rejectConfirmTitle'),
        text: this.$t('rejectConfirmText'),
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('reject'),
        cancelButtonText: this.$t('cancel'),
      }).then(result => {
        if (result.value) this.submit('canceled');
      });
    },
    submit(status) {
      // status = 'confirmed' | 'canceled' | null (chỉ save không đổi status)
      this.processing = true;
      const loader = this.$loading.show();

      const isCreate = !this.formModel._id;
      const url = isCreate ? this.service : `${this.service}/${this.formModel._id}`;
      const method = isCreate ? 'post' : 'put';
      const payload = Object.assign({}, this.formModel);
      if (status) {
        payload.status = status;
      }

      this.$service[method](url, payload).then(response => {
        if (response && response.data) {
          let successText;
          if (status === 'confirmed') successText = this.$t('approveSuccess');
          else if (status === 'canceled') successText = this.$t('rejectSuccess');
          else successText = this.$t('updateSuccess');

          this.$swal({
            title: this.$t('success'),
            text: successText,
            icon: 'success',
            toast: true,
            timer: 4000,
            position: 'top-end',
          });
          this.showForm = false;
          if (this.reloadBus) this.$bus.$emit(this.reloadBus);
        }
      }).catch(error => {
        if (error.response && error.response.data && error.response.data.message) {
          this.$swal({text: error.response.data.message, icon: 'error'});
        } else {
          this.formErrors = (error.response && error.response.data) || [];
        }
      }).finally(() => {
        this.processing = false;
        loader.hide();
      });
    },
    getImageUrl(prop) {
      if (this.formModel[prop]) {
        this.imageLoading = true;
        this.$service.get(this.service + '/images/' + this.formModel[prop])
            .then(response => {
              this.formModel[prop + 'Url'] = response.data;
            })
            .catch(() => {
              this.formModel[prop + 'Url'] = null;
            })
            .finally(() => {
              this.imageLoading = false;
            })
      } else {
        this.formModel[prop + 'Url'] = null;
      }
    },
  },
  i18n: {
    messages: {
      en: {
        modalTitle: 'Review KYC profile',
        // Validation banner
        readyToApprove: 'Profile complete — ready to approve',
        missingRequired: 'Missing {0} required item(s)',
        missingRequiredHint: ' — please ask the customer to provide these before approving',
        warningsFound: '{0} warning(s) found',
        warningsHint: ' — verify before approving',
        cannotApproveTitle: 'Cannot approve — missing required info',
        cannotApproveText: 'The customer must provide the following before approval:',
        approveWithWarningsTitle: 'Approve with warnings?',
        approveWithWarningsText: 'Please verify the following:',
        approveAnyway: 'Approve anyway',
        // Missing field messages
        'missing.type': 'Customer type not selected',
        'missing.IDFront': 'ID Front photo missing',
        'missing.IDBack': 'ID Back photo missing',
        'missing.owner': 'Representative photo missing',
        'missing.proofOfAddress': 'Proof of address photo missing',
        'missing.businessRegistration': 'Business registration photo missing',
        'missing.companyName': 'Company name missing',
        'missing.companyTaxCode': 'Business tax code missing',
        'missing.companyAddress': 'Head office address missing',
        'missing.position': 'Legal representative position missing',
        'missing.representativeName': 'Representative full name missing',
        'missing.fullName': 'Full name missing',
        'missing.representativeAddress': 'Representative permanent address missing',
        'missing.address': 'Address missing',
        'missing.callScript': 'Customer call script missing',
        'missing.invoiceEmail': 'Invoice enabled but no invoice email provided',
        'missing.personalTaxCode': 'Invoice enabled (personal) but personal tax code missing',
        // Warnings
        'warning.shortName': 'Short name not provided (optional but useful for contracts)',
        'warning.signerEmail': 'Contract signer email missing — will fall back to invoice email',
        'warning.companyNameMismatch': 'Company name in KYC ("{1}") differs from registered Client ("{0}") — verify',
        'warning.taxCodeMismatch': 'Business tax code in KYC ("{1}") differs from Client registration ("{0}") — verify',
        // Reject templates
        rejectTplLabel: 'Quick rejection reasons',
        rejectTplHint: 'Click to fill the note field',
        'rejectTpl.idBlurry': 'ID photo is blurry, please re-upload',
        'rejectTpl.taxCodeMismatch': 'Tax code does not match registration',
        'rejectTpl.companyNameMismatch': 'Company name does not match Business Registration',
        'rejectTpl.addressIncomplete': 'Address is incomplete (missing house number / district)',
        'rejectTpl.businessRegistrationInvalid': 'Business registration document is invalid / unclear',
        // Existing
        kycImagesPreview: 'KYC documents preview',
        kycInformation: 'KYC documents (upload)',
        generalInfo: 'General info',
        legalInfo: 'Legal info (for contract)',
        personalTaxCodeLabel: 'Personal tax code',
        personalAddressLabel: 'Permanent address',
        invoiceInformation: 'E-invoice info',
        invoiceSection: 'E-invoice info',
        reviewSection: 'Review decision',
        reviewNote: 'Review note',
        reviewNoteHint: 'Required when rejecting (provide reason)',
        notePlaceholder: 'Note or rejection reason',
        callScriptHint: 'Required for approval. Used by support staff when calling the customer.',
        callScriptPlaceholder: 'How does the customer use Callio? What is the support need?',
        alreadyConfirmed: 'Profile already approved',
        alreadyCanceled: 'Profile already rejected',
        reason: 'Reason',
        updateSuccess: 'Update success',
        success: 'Success',
        note: 'Note',
        businessRegistration: 'Business Registration',
        contract: 'Contract',
        contractSigned: 'Signed contract',
        IDFront: 'ID Front',
        IDBack: 'ID Back',
        ownerPicture: 'Representative photo',
        owner: 'Representative photo',
        customer: 'Customer',
        personal: 'Personal',
        enterprise: 'Enterprise',
        proofOfAddress: 'Proof of address',
        cancel: 'Cancel',
        close: 'Close',
        saveOnly: 'Save',
        approve: 'Approve',
        reject: 'Reject',
        saveConfirmTitle: 'Save changes?',
        saveConfirmText: 'Save changes without changing approval status.',
        approveConfirmTitle: 'Approve this profile?',
        approveConfirmText: 'The customer will be notified by email and in-app.',
        rejectConfirmTitle: 'Reject this profile?',
        rejectConfirmText: 'The customer will be notified with the reason.',
        rejectNoteRequiredTitle: 'Please enter rejection reason',
        rejectNoteRequiredText: 'A reason note is required when rejecting.',
        callScriptRequiredTitle: 'Call script required',
        callScriptRequiredText: 'Please enter the customer call script before approving.',
        approveSuccess: 'Profile approved. Customer notified.',
        rejectSuccess: 'Profile rejected. Customer notified with reason.',
        address: 'Address',
        taxCode: 'Tax code',
        invoiceEmails: 'Invoice emails',
        invoiceEnabled: 'Wants e-invoice',
        canceled: 'Reject',
        confirmed: 'Approve',
        companyInfo: 'Company info',
        ownerInfo: 'Legal representative',
        personalInfo: 'Personal info',
        fullName: 'Full name',
        shortName: 'Short name',
        position: 'Position (legal representative)',
        signerEmail: 'Contract signer email',
        callScript: 'Customer call script',
        offBuyNumber: 'Disable auto-buy phone number for this client',
      },
      vi: {
        modalTitle: 'Duyệt hồ sơ khách hàng',
        // Validation banner
        readyToApprove: 'Hồ sơ đầy đủ — sẵn sàng duyệt',
        missingRequired: 'Thiếu {0} thông tin bắt buộc',
        missingRequiredHint: ' — yêu cầu khách bổ sung trước khi duyệt',
        warningsFound: 'Có {0} cảnh báo',
        warningsHint: ' — kiểm tra trước khi duyệt',
        cannotApproveTitle: 'Không thể duyệt — thiếu thông tin',
        cannotApproveText: 'Khách cần bổ sung các thông tin sau trước khi được duyệt:',
        approveWithWarningsTitle: 'Duyệt dù có cảnh báo?',
        approveWithWarningsText: 'Vui lòng kiểm tra các điểm sau:',
        approveAnyway: 'Duyệt vẫn',
        // Missing field messages
        'missing.type': 'Chưa chọn loại khách hàng',
        'missing.IDFront': 'Thiếu ảnh CCCD mặt trước',
        'missing.IDBack': 'Thiếu ảnh CCCD mặt sau',
        'missing.owner': 'Thiếu ảnh người đại diện',
        'missing.proofOfAddress': 'Thiếu ảnh xác minh địa chỉ',
        'missing.businessRegistration': 'Thiếu ảnh đăng ký kinh doanh',
        'missing.companyName': 'Thiếu tên công ty',
        'missing.companyTaxCode': 'Thiếu mã số thuế doanh nghiệp',
        'missing.companyAddress': 'Thiếu địa chỉ trụ sở',
        'missing.position': 'Thiếu chức vụ người đại diện',
        'missing.representativeName': 'Thiếu họ tên người đại diện',
        'missing.fullName': 'Thiếu họ tên',
        'missing.representativeAddress': 'Thiếu địa chỉ thường trú người đại diện',
        'missing.address': 'Thiếu địa chỉ',
        'missing.callScript': 'Thiếu kịch bản gọi của khách',
        'missing.invoiceEmail': 'Có nhu cầu xuất hoá đơn nhưng chưa có email nhận',
        'missing.personalTaxCode': 'Có nhu cầu xuất hoá đơn (cá nhân) nhưng thiếu MST cá nhân',
        // Warnings
        'warning.shortName': 'Chưa có tên viết tắt (không bắt buộc nhưng hữu ích cho hợp đồng)',
        'warning.signerEmail': 'Thiếu email ký HĐ — sẽ dùng email hoá đơn fallback',
        'warning.companyNameMismatch': 'Tên công ty trong KYC ("{1}") khác đăng ký Client ("{0}") — cần verify',
        'warning.taxCodeMismatch': 'MST trong KYC ("{1}") khác đăng ký Client ("{0}") — cần verify',
        // Reject templates
        rejectTplLabel: 'Lý do từ chối phổ biến',
        rejectTplHint: 'Click để điền vào ô ghi chú',
        'rejectTpl.idBlurry': 'Ảnh CCCD mờ, vui lòng chụp lại',
        'rejectTpl.taxCodeMismatch': 'MST không khớp với đăng ký',
        'rejectTpl.companyNameMismatch': 'Tên công ty không khớp với Giấy phép ĐKKD',
        'rejectTpl.addressIncomplete': 'Địa chỉ chưa đầy đủ (thiếu số nhà / quận huyện)',
        'rejectTpl.businessRegistrationInvalid': 'Giấy phép ĐKKD không hợp lệ / không rõ',
        // Existing
        kycImagesPreview: 'Xem nhanh ảnh hồ sơ',
        kycInformation: 'Hồ sơ định danh (upload)',
        generalInfo: 'Thông tin chung',
        legalInfo: 'Thông tin pháp lý (cho hợp đồng)',
        personalTaxCodeLabel: 'MST cá nhân',
        personalAddressLabel: 'Địa chỉ thường trú',
        invoiceInformation: 'Thông tin xuất hoá đơn',
        invoiceSection: 'Thông tin xuất hoá đơn',
        reviewSection: 'Quyết định duyệt',
        reviewNote: 'Ghi chú duyệt',
        reviewNoteHint: 'Bắt buộc khi từ chối (nêu lý do)',
        notePlaceholder: 'Ghi chú hoặc lý do từ chối',
        callScriptHint: 'Bắt buộc khi duyệt. Dùng cho nhân viên hỗ trợ khi gọi cho khách.',
        callScriptPlaceholder: 'Khách dùng Callio để làm gì? Nhu cầu hỗ trợ là gì?',
        alreadyConfirmed: 'Hồ sơ đã được duyệt',
        alreadyCanceled: 'Hồ sơ đã bị từ chối',
        reason: 'Lý do',
        updateSuccess: 'Cập nhật thành công',
        success: 'Thành công',
        note: 'Ghi chú',
        businessRegistration: 'Đăng ký kinh doanh',
        contract: 'Hợp đồng',
        contractSigned: 'Hợp đồng đã ký',
        IDFront: 'CCCD mặt trước',
        IDBack: 'CCCD mặt sau',
        ownerPicture: 'Ảnh người đại diện',
        owner: 'Ảnh người đại diện',
        customer: 'Khách hàng',
        personal: 'Cá nhân',
        enterprise: 'Doanh nghiệp',
        proofOfAddress: 'Xác minh địa chỉ',
        cancel: 'Huỷ',
        close: 'Đóng',
        saveOnly: 'Lưu',
        approve: 'Duyệt hồ sơ',
        reject: 'Từ chối',
        saveConfirmTitle: 'Lưu thay đổi?',
        saveConfirmText: 'Chỉ lưu chỉnh sửa, không đổi trạng thái duyệt.',
        approveConfirmTitle: 'Duyệt hồ sơ này?',
        approveConfirmText: 'Khách sẽ nhận được email và thông báo in-app.',
        rejectConfirmTitle: 'Từ chối hồ sơ này?',
        rejectConfirmText: 'Khách sẽ nhận được thông báo kèm lý do.',
        rejectNoteRequiredTitle: 'Vui lòng nhập lý do từ chối',
        rejectNoteRequiredText: 'Cần ghi chú lý do khi từ chối hồ sơ.',
        callScriptRequiredTitle: 'Cần kịch bản gọi',
        callScriptRequiredText: 'Vui lòng nhập kịch bản gọi của khách trước khi duyệt.',
        approveSuccess: 'Đã duyệt hồ sơ. Khách đã được thông báo.',
        rejectSuccess: 'Đã từ chối hồ sơ. Khách đã được thông báo kèm lý do.',
        address: 'Địa chỉ',
        taxCode: 'Mã số thuế',
        invoiceEmails: 'Email nhận hoá đơn',
        invoiceEnabled: 'Đồng ý nhận xuất hoá đơn',
        canceled: 'Từ chối',
        confirmed: 'Duyệt',
        companyInfo: 'Thông tin công ty',
        ownerInfo: 'Người đại diện theo pháp luật',
        personalInfo: 'Thông tin cá nhân',
        fullName: 'Họ và tên',
        shortName: 'Tên viết tắt',
        position: 'Chức vụ (người đại diện theo PL)',
        signerEmail: 'Email ký hợp đồng',
        callScript: 'Kịch bản gọi của khách',
        offBuyNumber: 'Khoá chức năng tự mua số điện thoại',
      }
    }
  }
}
</script>

<style scoped>
.kyc-section {
  border: 1px solid #e3e6f0;
  border-radius: 6px;
  padding: 12px 16px;
  background: #fff;
  margin-bottom: 0;
}

.kyc-legend {
  width: auto;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #5a5c69;
  padding: 2px 8px;
  margin-bottom: 8px;
  background: #f8f9fc;
  border-radius: 4px;
  border: 1px solid #e3e6f0;
}

.kyc-subheader {
  font-size: 0.875rem;
  font-weight: 600;
  color: #4e73df;
  margin-top: 12px;
  margin-bottom: 8px;
  padding-bottom: 4px;
  border-bottom: 1px dashed #e3e6f0;
}
</style>
